import { skipToken } from "@reduxjs/toolkit/query";
import type { TestSignalProposalToolOutput } from "@repo/app-types/entities/configurationAgent";
import {
  copilotProposalIdSchema,
  type CopilotProposal,
} from "@repo/app-types/entities/copilotProposal";
import { defaultDateRange } from "@repo/app-types/entities/dateRange";
import type { PlaybookSelection } from "@repo/app-types/entities/playbook";
import type {
  SignalDraft,
  SignalId,
  SignalMetadataSchema,
} from "@repo/app-types/entities/signal";
import { PageHeader } from "@repo/ui/Navigation/pageHeader";
import { PageWidth } from "@repo/ui/Navigation/pageWidth";
import { DateTime } from "luxon";
import { useCallback, useEffect, useMemo, useState, type FC } from "react";
import { useParams, useSearchParams } from "react-router";
import {
  useGetPendingSignalProposalQuery,
  useResolveProposalMutation,
} from "../../../api/entities/copilotProposal.js";
import {
  useCreateSignalMutation,
  useGetSignalQuery,
  useTestSignalProposalMutation,
  useUpdateSignalMutation,
} from "../../../api/entities/signal.js";
import { useResearchAgentProps } from "../../researchAgent/researchAgentContext.js";
import { SignalEditorContainer } from "./signalEditorContainer.js";
import { SIGNAL_PROPOSAL_QUERY_PARAM } from "./signalEditorUrlState.js";
import { cleanMetadataSchema, isSignalFormDirty } from "./signalFormHelpers.js";
import {
  SignalTestConfigModal,
  type TestSignalFiltersUi,
} from "./signalTestConfigModal.js";
import { SignalTestMatchesModal } from "./signalTestMatchesModal.js";

type TestResultsState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "error" }
  | {
      status: "complete";
      data: TestSignalProposalToolOutput;
      completedAt: DateTime;
    };

export const SignalEditorPage: FC<{ onClose: () => void }> = ({ onClose }) => {
  const { signalId } = useParams<{ signalId: SignalId }>();
  const isNew = !signalId;

  const {
    data: signal,
    isLoading: isLoadingSignal,
    isError,
  } = useGetSignalQuery(isNew ? skipToken : signalId);
  const [createSignal, { isLoading: isCreating }] = useCreateSignalMutation();
  const [updateSignal, { isLoading: isSaving }] = useUpdateSignalMutation();
  const [testSignalProposal] = useTestSignalProposalMutation();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [metadataSchema, setMetadataSchema] = useState<SignalMetadataSchema>({
    fields: [],
  });
  const [playbookScope, setPlaybookScope] = useState<PlaybookSelection>({
    type: "all",
  });
  const [error, setError] = useState<string | null>(null);
  const [initialized, setInitialized] = useState(isNew);
  const [testResultsState, setTestResultsState] = useState<TestResultsState>({
    status: "idle",
  });
  // Within-session memory of the filters used on the last actual run. When
  // non-null, the main Test signal click re-runs with these directly; otherwise
  // it opens the config modal so the user can pick.
  const [lastRunFilters, setLastRunFilters] =
    useState<TestSignalFiltersUi | null>(null);
  const [isTestConfigOpen, setIsTestConfigOpen] = useState(false);
  const [isMatchesModalOpen, setIsMatchesModalOpen] = useState(false);

  // Until a test is explicitly configured, tests follow the form's current
  // playbook scope. Memoized because the config modal re-seeds its local state
  // whenever this object's identity changes.
  const effectiveTestFilters: TestSignalFiltersUi = useMemo(
    () =>
      lastRunFilters ?? {
        playbookScope,
        teammateGroupIds: null,
        dateRange: defaultDateRange,
      },
    [lastRunFilters, playbookScope],
  );

  const runTestWithFilters = useCallback(
    async (filters: TestSignalFiltersUi) => {
      if (!name.trim() || !description.trim()) return;

      setTestResultsState({ status: "loading" });

      const now = DateTime.now();
      const rangeEnd = filters.dateRange.end ?? now;
      const rangeStart = rangeEnd.minus(filters.dateRange.length);

      try {
        const data = await testSignalProposal({
          ...(signalId ? { signalId } : {}),
          signal: {
            name,
            description,
            metadata_schema: cleanMetadataSchema(metadataSchema),
            playbook_scope: playbookScope,
          },
          filters: {
            playbookScope: filters.playbookScope,
            ...(filters.teammateGroupIds
              ? { teammateGroupIds: filters.teammateGroupIds }
              : {}),
            lastRecordedAtRange: {
              after: rangeStart.startOf("day").toUTC().toISO(),
              before: rangeEnd.endOf("day").toUTC().toISO(),
            },
          },
        }).unwrap();

        setTestResultsState({
          status: "complete",
          data,
          completedAt: DateTime.now(),
        });
      } catch (err) {
        console.error("Failed to test signal:", err);
        setTestResultsState({ status: "error" });
      }
    },
    [
      signalId,
      name,
      description,
      metadataSchema,
      playbookScope,
      testSignalProposal,
    ],
  );

  const handleRunTestFromModal = useCallback(
    (filters: TestSignalFiltersUi) => {
      setIsTestConfigOpen(false);
      setLastRunFilters(filters);
      runTestWithFilters(filters);
    },
    [runTestWithFilters],
  );

  // Open config modal if filters have not been configured yet
  const handleTestSignalClick = useCallback(() => {
    if (lastRunFilters) {
      runTestWithFilters(lastRunFilters);
    } else {
      setIsTestConfigOpen(true);
    }
  }, [lastRunFilters, runTestWithFilters]);

  const handleConfigureTestClick = useCallback(() => {
    setIsTestConfigOpen(true);
  }, []);

  useEffect(() => {
    if (signal && !initialized) {
      setName(signal.name);
      setDescription(signal.description);
      setMetadataSchema(signal.metadata_schema);
      setPlaybookScope(signal.playbook_scope);
      setInitialized(true);
    }
  }, [signal, initialized]);

  // The form as Ask Alpharun reads it at each send from this page.
  const signalDraft = useMemo<SignalDraft>(
    () => ({
      signalId,
      name,
      description,
      metadata_schema: cleanMetadataSchema(metadataSchema),
      playbook_scope: playbookScope,
    }),
    [signalId, name, description, metadataSchema, playbookScope],
  );
  const publishSignalDraft = useResearchAgentProps()?.publishSignalDraft;
  useEffect(() => {
    if (initialized) {
      publishSignalDraft?.(signalDraft);
    }
  }, [publishSignalDraft, initialized, signalDraft]);
  useEffect(() => () => publishSignalDraft?.(undefined), [publishSignalDraft]);

  // One-shot deep link from an Ask Alpharun proposal card (?proposalId=...):
  // the proposal's part is laid over the form once the saved signal has
  // loaded, and its evidence becomes the test result. The rest of the form
  // stays as the user has it, which is what the proposal was tested against.
  // Apply / Discard resolve the row; the save itself is ordinary, except that
  // a new signal keeps the id its proposal minted. A proposal for another
  // signal loads nothing. While the link is unresolved the form is held: no
  // banner, no save.
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkProposalId = copilotProposalIdSchema.safeParse(
    searchParams.get(SIGNAL_PROPOSAL_QUERY_PARAM),
  ).data;
  const {
    currentData: deepLinkProposal,
    isError: isDeepLinkProposalUnavailable,
  } = useGetPendingSignalProposalQuery(deepLinkProposalId ?? skipToken);
  const isLoadingProposal = deepLinkProposalId !== undefined;
  const clearDeepLinkProposalId = useCallback(() => {
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete(SIGNAL_PROPOSAL_QUERY_PARAM);
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);
  const [resolveProposal, { isLoading: isResolvingProposal }] =
    useResolveProposalMutation();
  const [proposal, setProposal] = useState<CopilotProposal<"signal"> | null>(
    null,
  );
  const [isProposalPending, setIsProposalPending] = useState(false);
  useEffect(() => {
    if (deepLinkProposalId && isDeepLinkProposalUnavailable) {
      clearDeepLinkProposalId();
      setError("This suggestion is no longer available.");
    }
  }, [
    deepLinkProposalId,
    isDeepLinkProposalUnavailable,
    clearDeepLinkProposalId,
  ]);
  useEffect(() => {
    if (
      !initialized ||
      !deepLinkProposalId ||
      !deepLinkProposal ||
      deepLinkProposal.id !== deepLinkProposalId
    ) {
      return;
    }
    clearDeepLinkProposalId();
    const { data } = deepLinkProposal;
    const targetsThisPage =
      data.kind === "create_signal"
        ? isNew
        : deepLinkProposal.resource_id === signalId;
    if (!targetsThisPage) {
      return;
    }
    setTestResultsState({ status: "idle" });
    setError(null);
    switch (data.kind) {
      case "create_signal":
        setName(data.name);
        setDescription(data.description);
        setMetadataSchema(data.metadata_schema);
        break;
      case "update_signal_name":
        setName(data.name);
        break;
      case "update_signal_description":
        setDescription(data.description);
        break;
      case "update_signal_metadata":
        setMetadataSchema(data.metadata_schema);
        break;
    }
    if ("evidence" in data) {
      setTestResultsState({
        status: "complete",
        data: data.evidence,
        completedAt: DateTime.fromISO(deepLinkProposal.created_at),
      });
    }
    setProposal(deepLinkProposal);
    setIsProposalPending(true);
  }, [
    initialized,
    deepLinkProposalId,
    deepLinkProposal,
    isNew,
    signalId,
    clearDeepLinkProposalId,
  ]);

  if (!isNew && (isLoadingSignal || !initialized)) {
    return (
      <div className="flex flex-col">
        <PageHeader width={PageWidth.Form} isLoading />
      </div>
    );
  }

  if (!isNew && (isError || !signal)) {
    return (
      <div className="flex flex-col">
        <PageHeader width={PageWidth.Form} title="Signal not found" />
      </div>
    );
  }

  const isFormValid = name.trim() !== "" && description.trim() !== "";
  const hasChanged =
    !isNew &&
    signal != null &&
    isSignalFormDirty(signal, name, description, metadataSchema, playbookScope);

  const isProposalBusy = isLoadingProposal || isResolvingProposal;

  // Accepting resolves the row; the form keeps the changes as unsaved edits.
  const handleApplyProposal = async () => {
    if (!proposal || !isProposalPending || isProposalBusy) return;
    try {
      await resolveProposal({
        proposalId: proposal.id,
        status: "applied",
      }).unwrap();
      setIsProposalPending(false);
    } catch (err) {
      console.error("Failed to accept the suggestion:", err);
      setError("Failed to accept the suggestion. Please try again.");
    }
  };

  const handleSubmit = async () => {
    if (!isFormValid || isProposalBusy) return;
    if (!isNew && !hasChanged) return;

    const payload = {
      name: name.trim(),
      description: description.trim(),
      metadata_schema: cleanMetadataSchema(metadataSchema),
      playbook_scope: playbookScope,
    };

    try {
      if (isNew) {
        await createSignal({
          ...payload,
          ...(proposal ? { proposal_id: proposal.id } : {}),
        }).unwrap();
      } else {
        await updateSignal({
          id: signal!.id,
          ...payload,
          expected_version_id: signal!.live_version_id ?? undefined,
        }).unwrap();
      }
      // Saving a still-pending proposal's changes accepts it. The signal is
      // saved either way; a failure here only leaves the card pending.
      if (proposal && isProposalPending) {
        await resolveProposal({ proposalId: proposal.id, status: "applied" })
          .unwrap()
          .catch((err: unknown) => {
            console.error("Failed to mark the suggestion accepted:", err);
          });
      }
      onClose();
    } catch (err: any) {
      const action = isNew ? "create" : "update";
      console.error(`Failed to ${action} signal:`, err);
      setError(
        err.data?.message || `Failed to ${action} signal. Please try again.`,
      );
    }
  };

  const handleDiscard = () => {
    if (!signal) return;
    setName(signal.name);
    setDescription(signal.description);
    setMetadataSchema(signal.metadata_schema);
    setPlaybookScope(signal.playbook_scope);
    setError(null);
  };

  const handleDiscardProposal = async () => {
    if (!proposal || isProposalBusy) return;
    try {
      await resolveProposal({
        proposalId: proposal.id,
        status: "discarded",
      }).unwrap();
    } catch (err) {
      console.error("Failed to discard the suggestion:", err);
      setError("Failed to discard the suggestion. Please try again.");
      return;
    }
    setProposal(null);
    setIsProposalPending(false);
    setTestResultsState({ status: "idle" });
    if (signal) {
      handleDiscard();
    } else {
      setName("");
      setDescription("");
      setMetadataSchema({ fields: [] });
    }
  };

  const testResultPillState =
    testResultsState.status === "idle" ? null : testResultsState;

  return (
    <>
      <SignalEditorContainer
        name={name}
        description={description}
        metadataSchema={metadataSchema}
        playbookScope={playbookScope}
        error={error}
        onNameChange={setName}
        onDescriptionChange={setDescription}
        onMetadataSchemaChange={setMetadataSchema}
        onPlaybookScopeChange={setPlaybookScope}
        onClose={onClose}
        onDiscard={
          isNew
            ? undefined
            : isProposalPending
              ? handleDiscardProposal
              : handleDiscard
        }
        onSubmit={handleSubmit}
        isLoading={isNew ? isCreating : isSaving}
        isSubmitDisabled={
          isProposalBusy || (isNew ? !isFormValid : !isFormValid || !hasChanged)
        }
        submitLabel={isNew ? "Create" : "Save"}
        showDiscard={!isNew && hasChanged && !isProposalBusy}
        onTestSignalClick={handleTestSignalClick}
        onConfigureTestClick={handleConfigureTestClick}
        isTestSignalDisabled={
          !isFormValid || testResultsState.status === "loading"
        }
        testResultPillState={testResultPillState}
        onViewMatches={() => setIsMatchesModalOpen(true)}
        suggestion={
          proposal && isProposalPending && !isLoadingProposal
            ? {
                isNewSignal: isNew,
                baseline: signal,
                onDiscard: handleDiscardProposal,
                onApply: handleApplyProposal,
                isResolving: isResolvingProposal,
              }
            : undefined
        }
      />

      <SignalTestConfigModal
        isOpen={isTestConfigOpen}
        onClose={() => setIsTestConfigOpen(false)}
        initialFilters={effectiveTestFilters}
        onRunTest={handleRunTestFromModal}
      />

      {testResultsState.status === "complete" ? (
        <SignalTestMatchesModal
          isOpen={isMatchesModalOpen}
          onClose={() => setIsMatchesModalOpen(false)}
          data={testResultsState.data}
          metadataSchema={metadataSchema}
        />
      ) : null}
    </>
  );
};
